import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Printer, Ban, IndianRupee } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { openPdf } from "@/lib/print";

const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT", "STORES"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Dispatches() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const canReceive = ["ORG_ADMIN", "ACCOUNTS"].includes(user?.role);
  const { companies } = useCompany() || {};
  const [dispatches, setDispatches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const safe = (p) => p.catch(() => ({ data: [] }));
      const [{ data: d }, { data: w }, { data: it }, { data: pr }, { data: cl }, { data: tk }] = await Promise.all([
        api.get("/production-dispatches"), api.get("/inventory/warehouses"), api.get("/inventory/items"), safe(api.get("/projects")),
        safe(api.get("/revenue/clients")), safe(api.get("/weighbridge-tickets")),
      ]);
      setDispatches(d); setWarehouses(w); setItems(it); setProjects(pr); setClients(cl); setTickets(tk);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const itemLabel = (id) => { const i = items.find((x) => x.id === id); return i ? `${i.code} — ${i.name}` : "—"; };
  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || "—";
  const projectLabel = (id) => { const p = projects.find((x) => x.id === id); return p ? `${p.code} — ${p.name}` : "—"; };

  const cancel = async (d) => {
    if (!window.confirm(`Cancel dispatch ${d.number}? Stock comes back to ${warehouseName(d.warehouse_id)} and the cost / invoice entries are reversed.`)) return;
    try {
      await api.post(`/production-dispatches/${d.id}/cancel`);
      toast.success(`${d.number} cancelled`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="dispatches-page">
      <PageHeader
        title="Dispatches"
        description="Finished goods leaving the plant store — to your own project (charged at production cost) or sold to a client (tax invoice, receivable)."
        action={canWrite && <CreateDispatchDialog companies={companies} warehouses={warehouses} items={items} projects={projects} clients={clients} tickets={tickets} onCreated={load} />}
      />
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        {!loading && dispatches.length === 0 ? (
          <EmptyState title="No dispatches yet" testId="dispatches-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead>Item</TableHead><TableHead>To</TableHead>
                <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Invoice</TableHead><TableHead /></TableRow>
            </TableHeader>
            <TableBody>
              {dispatches.map((d) => {
                const sale = d.rate !== null && d.rate !== undefined;
                const due = Number(d.invoice_total) - Number(d.received_amount);
                return (
                  <TableRow key={d.id} className={d.status === "CANCELLED" ? "opacity-50" : ""} data-testid={`dispatch-row-${d.number}`}>
                    <TableCell className="font-mono text-xs text-foreground">{d.number}{d.status === "CANCELLED" && <div className="font-sans text-[10px] text-rose-500">Cancelled</div>}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(d.dispatch_date)}</TableCell>
                    <TableCell className="text-xs text-foreground">{itemLabel(d.item_id)}<div className="text-[10px] text-muted-foreground">from {warehouseName(d.warehouse_id)}{d.vehicle_no ? ` · ${d.vehicle_no}` : ""}</div></TableCell>
                    <TableCell className="text-xs">
                      {sale ? <><span className="rounded bg-sky-500/10 px-1 text-[10px] text-sky-600">Sale</span> {d.client_name}</> : d.project_id ? projectLabel(d.project_id) : (d.client_name || "—")}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(d.quantity, 3)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(d.total_cost, 2)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {sale ? (<>
                        <div className="font-semibold text-foreground">{inr(d.invoice_total)}</div>
                        <div className={`text-[10px] ${due > 0.005 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`}>{due > 0.005 ? `due ${inr(due)}` : "paid"}</div>
                      </>) : "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <Button size="sm" variant="ghost" title={sale ? "Tax invoice & challan" : "Delivery challan"} onClick={() => openPdf(`/production-dispatches/${d.id}/challan`)}><Printer className="h-3.5 w-3.5" /></Button>
                      {sale && due > 0.005 && d.status === "POSTED" && canReceive && <ReceiveDialog dispatch={d} due={due} onDone={load} />}
                      {d.status === "POSTED" && canWrite && Number(d.received_amount) === 0 && <Button size="sm" variant="ghost" title="Cancel" onClick={() => cancel(d)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function ReceiveDialog({ dispatch, due, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ receipt_date: todayISO(), amount: due.toFixed(2), reference: "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/production-dispatches/${dispatch.id}/receive`, { ...form, reference: form.reference || null });
      toast.success("Payment recorded");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" title="Record payment received"><IndianRupee className="h-3.5 w-3.5 text-emerald-500" /></Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Payment for {dispatch.number}</DialogTitle><DialogDescription>{dispatch.client_name} · due {inr(due)}</DialogDescription></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" value={form.receipt_date} onChange={(e) => setForm({ ...form, receipt_date: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" min="0.01" max={due} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label>UTR / cheque no.</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Record</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateDispatchDialog({ companies, warehouses, items, projects, clients, tickets, onCreated }) {
  const EMPTY = { company_id: "", warehouse_id: "", item_id: "", mode: "PROJECT", project_id: "", client_id: "", client_name: "",
    rate: "", gst_pct: "18", dispatch_date: todayISO(), quantity: "", vehicle_no: "", weighbridge_ticket_id: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const sale = form.mode === "SALE";
  const taxable = Number(form.quantity || 0) * Number(form.rate || 0);
  const gst = (taxable * Number(form.gst_pct || 0)) / 100;
  const finished = items.filter((i) => i.category === "STOCK" && i.is_active !== false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        company_id: form.company_id, warehouse_id: form.warehouse_id, item_id: form.item_id, dispatch_date: form.dispatch_date,
        quantity: form.quantity, vehicle_no: form.vehicle_no || null, weighbridge_ticket_id: form.weighbridge_ticket_id || null,
        project_id: sale ? null : form.project_id || null,
        client_id: sale ? form.client_id || null : null, client_name: sale ? form.client_name || null : null,
        rate: sale ? form.rate : null, gst_pct: sale ? form.gst_pct || "0" : "0",
      };
      const { data } = await api.post("/production-dispatches", payload);
      toast.success(`Dispatch ${data.number} created`);
      setForm(EMPTY);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-dispatch-button"><Plus className="h-4 w-4" /> New Dispatch</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader><DialogTitle>New Dispatch</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-dispatch-form">
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={!sale ? "default" : "outline"} onClick={() => setForm({ ...form, mode: "PROJECT" })}>To my project</Button>
            <Button type="button" size="sm" variant={sale ? "default" : "outline"} onClick={() => setForm({ ...form, mode: "SALE" })}>Sale to a client</Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>From store (warehouse)</Label>
              <Select required value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Finished item</Label>
            <Select required value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{finished.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {sale ? (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Client</Label>
                  <Select value={form.client_id || "typed"} onValueChange={(v) => setForm({ ...form, client_id: v === "typed" ? "" : v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="typed">Type a name instead</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {!form.client_id && <div><Label>Client name</Label><Input required value={form.client_name} onChange={set("client_name")} className="mt-1.5" /></div>}
                <div><Label>Selling rate per unit (₹)</Label><Input required type="number" step="0.01" min="0" value={form.rate} onChange={set("rate")} className="mt-1.5" /></div>
                <div><Label>GST %</Label><Input type="number" step="0.01" min="0" max="100" value={form.gst_pct} onChange={set("gst_pct")} className="mt-1.5" /></div>
              </div>
              <p className="text-xs text-muted-foreground">Invoice: {inr(taxable)} + GST {inr(gst)} = <b className="text-foreground">{inr(taxable + gst)}</b></p>
            </div>
          ) : (
            <div>
              <Label>Project</Label>
              <Select required value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">Charged to the project at production cost.</p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Date</Label><Input required type="date" value={form.dispatch_date} onChange={set("dispatch_date")} className="mt-1.5" /></div>
            <div><Label>Quantity</Label><Input required type="number" step="0.001" min="0.001" value={form.quantity} onChange={set("quantity")} className="mt-1.5" data-testid="dispatch-quantity-input" /></div>
            <div><Label>Vehicle no.</Label><Input value={form.vehicle_no} onChange={set("vehicle_no")} className="mt-1.5" /></div>
          </div>
          {tickets.length > 0 && (
            <div>
              <Label>Weighbridge ticket (optional)</Label>
              <Select value={form.weighbridge_ticket_id || "none"} onValueChange={(v) => setForm({ ...form, weighbridge_ticket_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {tickets.slice(0, 50).map((t) => <SelectItem key={t.id} value={t.id}>{t.ticket_number} · {t.vehicle_no || ""} · net {Number(t.net_weight)} kg</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
